import { Router } from '@angular/router';
import { Component, Input, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatSort, Sort } from '@angular/material/sort';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ApplicationInfoModel } from '../../models/application-info.model';
import { UserService } from 'app/users/user.service';
import { ApplicationInfoService, RetrieveApplRequest } from '../landing-page.service';
import { UserProfileRecord } from 'app/users/user-profile-record';


@Component({
  selector: 'app-submitted-applications-list',
  templateUrl: './submitted-applications-list.component.html',
  styleUrls: ['./submitted-applications-list.component.scss']
})
export class SubmittedApplicationsListComponent { 
  applicationsList!: ApplicationInfoModel[];
  private sizeChangeSubscription!: Subscription;
  private mobileDeviceSize: boolean = false;
  loadingComplete: boolean = false;
  appTypes: string[] = ['Submitted', 'In Review', 'Approved / Closed'];
  user!: UserProfileRecord;
  showResults: boolean = false;

  @ViewChild(MatPaginator) appPaginator!: MatPaginator;
  currentPage = 0;
  paginatedSubmittedAppl: ApplicationInfoModel[] = [];    // Items to show on current page
  pageSize = 10;
  totalItems = 0;
  @ViewChild(MatSort) sort!: MatSort;

  

  constructor(private responsive: BreakpointObserver, 
    private router: Router,
    private sortAnnouncer: LiveAnnouncer,
    protected userService: UserService,
    private applicationInfoService: ApplicationInfoService
  ) { }

  ngOnInit(): void {

      this.sizeChangeSubscription = this.responsive.observe([Breakpoints.Handset]).subscribe(result => {
        if(result.matches) {
          this.mobileDeviceSize = true;
        } else {
          this.mobileDeviceSize = false;
        }
      });
      this.loadingComplete = false;
      this.retrieveApplications('Submitted');
  }

  ngOnDestroy(): void {
    this.sizeChangeSubscription.unsubscribe();
  }

  get applications() {
    return this.applicationsList;
  }

  get isMobileDeviceSize(): boolean {
    return this.mobileDeviceSize;
  }

   onPageChange(event: PageEvent) {
      this.pageSize = event.pageSize;
      this.currentPage = event.pageIndex;
      this.updatePaginatedItems();
    }
   
     private updatePaginatedItems() {
      const start = this.currentPage * this.pageSize;
      const end = start + this.pageSize;
      this.totalItems = this.applicationsList.length;
      this.paginatedSubmittedAppl = this.applicationsList.slice(start, end);
      this.loadingComplete = true;
    }
    ngOnChanges(changes: SimpleChanges) {
      if ( this.applicationsList) { 
        this.totalItems = this.applicationsList.length;
        this.currentPage = 0; // reset to first page when new data comes inv
        this.updatePaginatedItems();
      }
    }

  viewSubmittedApplication(app: ApplicationInfoModel) {
    let type = app.form;
    if(!type) {
      // error here
      return;
    }
    type = this.parseFormType(type);
    this.router.navigate(['/usan/application'], 
      { queryParams : { formType: type, id: app.caseSysId, source: 'applicant-dashboard'}});
  }

  parseFormType(str: string) : string {
    if(str.includes('Form')) {
      return str.split('Form')[1].trim();
    }
    return str.trim();
  }

  sortChangeUpdate(sortChange: Sort) {
    const data = [... this.applicationsList];
    let column = sortChange.active;
    let dir = sortChange.direction;
    if (!column || dir === '') {
      //no change
      return;
    }
    const isAsc = dir ==='asc';
    this.applicationsList = data.sort((a,b) => {
      let aValue = typeof a[column] === 'string' ? a[column].toLowerCase() : a[column];
      let bValue = typeof b[column] === 'string' ? b[column].toLowerCase() : b[column];
      return this.compareValues(aValue, bValue, isAsc);
      
    });
    this.updatePaginatedItems();
    if(sortChange.direction) { //to announce sorting change for assistive technology
      this.sortAnnouncer.announce('Sorted '+ sortChange.direction);
    } else {
      this.sortAnnouncer.announce('Sort cleared');
    }
  }

  compareValues( a: string|number, b: string|number, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  selectAppTypeChange(appType: MatTabChangeEvent) {
    this.loadingComplete = false;
    let appTypeSelected = this.appTypes[appType.index];
    if(appTypeSelected.includes('Approved'))
      appTypeSelected = 'Approved'; //Approved will also include Closed cases as well
    this.retrieveApplications(appTypeSelected);
  }

  retrieveApplications(type: string) {
    this.showResults = false;
    this.user = this.userService.profile;
    if (this.userService.profile.orgId) { 
      const requestObj: RetrieveApplRequest = {
        orgId: this.userService.profile.orgId ?? '',
        ssoId: this.userService.profile.uniqueId ?? '',
        status: [type] 
      };
      this.applicationInfoService.fetchApplicationsByStatus(requestObj).subscribe({
        next: (response) => {
          let data = response.Data;
          if (data) {
            this.applicationsList  = this.parseResults(data);
            if(this.applicationsList) {
              this.currentPage = 0; 
              this.updatePaginatedItems();
            }   
          }
          this.loadingComplete = true;
          this.showResults = true;
        },
        error: (err) => {
          console.log("Fetching Applications Error - " + err);
          this.loadingComplete = true;
          this.showResults = true;
        }
      });
    }
  }

  parseResults(data): ApplicationInfoModel[] {
    const array: ApplicationInfoModel[] = [];
    for(const d of data) {
        array.push(ApplicationInfoModel.parseAPIValues(d));
    }
    return array;
  }

}
